import { useAuth } from '@matix/auth';

/**
 * How far the signed-in cashier may move a line's selling price.
 *
 * `Security_Users.ChangeSalesPriceMode`, the admin Users form's "Change
 * selling price" dropdown. It was written by that form and read by nothing for
 * as long as it has existed; a user set to "Full change allowed" got nothing
 * from it, and a user set to "No change allowed" was not stopped.
 *
 * **0 and NULL mean NOT CONFIGURED, and are ALLOWED.** This is the whole
 * subtlety of turning the setting on. 0 is not one of the three values the
 * dropdown offers — it is what the ERP leaves behind when nobody has chosen —
 * and it is what virtually every user on every tenant carries. A fleet probe
 * on 2026-09-16 found ~450 such users across the 21 tenants that allow price
 * editing at all (Oppa 180, Ponytail 133) against 13 holding an explicit 1.
 * Reading 0 as "no change" would have taken price editing away from all of
 * them on the strength of a setting nobody had ever set.
 *
 * The mode is the THIRD gate, not the only one. The tenant's
 * `IsTotalPriceChanges` flag and the `Edit Cart Item Price` permission still
 * have to pass first; this only narrows what an already-entitled cashier can
 * do.
 */
export type PriceChangeRule = 'none' | 'increase-only' | 'full';

export function priceChangeRuleFromMode(mode: number | null | undefined): PriceChangeRule {
  if (mode === 1) return 'none';
  if (mode === 2) return 'increase-only';
  // 3 = full, and 0 / null / undefined = never configured, which is allowed.
  return 'full';
}

/** The signed-in cashier's rule, from their token. */
export function usePriceChangeRule(): PriceChangeRule {
  const { cashier } = useAuth();
  return priceChangeRuleFromMode(cashier?.permissions?.change_sales_price_mode);
}

/**
 * May this line's price move from `original` to `next`?
 *
 * Compared on the UNIT price rather than the line total, so the answer does
 * not change with quantity — "increase only" means the customer is never
 * charged less per item than the catalog says, whether they buy one or ten.
 * A tiny epsilon absorbs the float noise of back-solving a unit price from an
 * edited total, which would otherwise read a re-typed identical total as a
 * decrease.
 */
export function isPriceChangeAllowed(
  rule: PriceChangeRule,
  original: number,
  next: number,
): boolean {
  if (rule === 'full') return true;
  if (rule === 'none') return Math.abs(next - original) < 1e-6;
  return next >= original - 1e-6;
}
